import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBadges } from '../context/BadgesContext';
import api from '../services/api';
import { roleLabel } from '../utils/roles';

function SidebarLink({ to, children, badge }) {
  return (
    <NavLink to={to}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
          isActive ? 'bg-brand text-white' : 'text-ink-secondary hover:bg-surface-inset hover:text-ink-primary'
        }`
      }>
      {children}
      {badge > 0 && (
        <span className="ml-auto bg-status-rejected text-white text-2xs font-semibold px-1.5 py-px rounded-full min-w-[18px] text-center">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout, isAdmin, isAuditor, isPrivileged } = useAuth();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  // Estado compartilhado com as paginas: elas chamam refresh() apos marcar
  // notificacao como lida ou confirmar leitura, e o menu acompanha na hora.
  const { notifications, unreadCount: notifCount, pendingReadings, refresh } = useBadges();

  async function handleMarkRead(id) {
    await api.patch(`/notifications/${id}/read`);
    refresh();
  }

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 bg-surface-base border-r fixed top-0 left-0 bottom-0 z-10 flex flex-col">
        <div className="px-4 py-4 border-b border-subtle flex items-center gap-2">
          <h1 className="text-lg font-bold text-brand tracking-tight">QDoc</h1>
          <span className="font-mono text-2xs text-ink-muted bg-surface-inset px-1.5 py-0.5 rounded">v1.0</span>
        </div>

        {/* O perfil de auditoria enxerga telas de gestao onde nada e
            editavel. Sem o aviso, a pessoa descobre a restricao clicando e
            levando 403 — que parece defeito, nao regra. */}
        {isAuditor && (
          <div className="mx-3 mt-3 px-3 py-2 rounded bg-status-pending/10 border border-status-pending/30">
            <div className="text-2xs font-semibold text-status-pending uppercase tracking-wider">
              Somente leitura
            </div>
            <div className="text-2xs text-ink-secondary mt-0.5">
              Perfil de auditoria: visualiza tudo, sem alterar nem baixar.
            </div>
          </div>
        )}

        <nav className="p-3 flex-1 overflow-y-auto space-y-4">
          <div>
            <div className="text-2xs font-semibold text-ink-muted uppercase tracking-wider px-3 mb-1">Principal</div>
            <SidebarLink to="/dashboard">Dashboard</SidebarLink>
            <SidebarLink to="/documents">Documentos</SidebarLink>
            <SidebarLink to="/folders">Pastas</SidebarLink>
            <SidebarLink to="/training" badge={pendingReadings}>Treinamento</SidebarLink>
          </div>

          {isPrivileged && (
            <div>
              <div className="text-2xs font-semibold text-ink-muted uppercase tracking-wider px-3 mb-1">Gestao</div>
              {(isAdmin || isAuditor) && <SidebarLink to="/users">Usuários</SidebarLink>}
              <SidebarLink to="/groups">Grupos</SidebarLink>
              <SidebarLink to="/taxonomy">Taxonomia</SidebarLink>
              <SidebarLink to="/approval-roles">Funções de Aprovação</SidebarLink>
            </div>
          )}

          {(isAdmin || isAuditor) && (
            <div>
              <div className="text-2xs font-semibold text-ink-muted uppercase tracking-wider px-3 mb-1">Administracao</div>
              <SidebarLink to="/settings">Configurações</SidebarLink>
              <SidebarLink to="/audit">Auditoria</SidebarLink>
            </div>
          )}
        </nav>

        <div className="p-3 border-t border-subtle">
          <div className="text-sm font-medium text-ink-primary">{user?.name}</div>
          <div className="text-xs text-ink-muted">{roleLabel(user?.role)}</div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-60 min-h-screen">
        {/* Top Bar */}
        <div className="h-[52px] border-b flex items-center justify-between px-6 bg-surface-100 sticky top-0 z-5">
          <div />
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 text-ink-secondary hover:text-ink-primary text-lg bg-transparent border-none cursor-pointer">
                &#128276;
                {notifCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-status-rejected text-white text-[10px] font-semibold min-w-[16px] h-4 rounded-full flex items-center justify-center">
                    {notifCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full w-80 bg-surface-100 border rounded-lg p-3 shadow-lg z-20">
                  <div className="font-semibold mb-2 text-sm">Notificações</div>
                  {notifications.length === 0 ? (
                    <div className="text-ink-muted text-sm p-3">Nenhuma notificação</div>
                  ) : (
                    notifications.slice(0, 5).map((n) => (
                      <div key={n.id} onClick={() => handleMarkRead(n.id)}
                        className={`p-2 border-b border-subtle cursor-pointer rounded mb-1 ${!n.read_at ? 'bg-surface-inset' : ''}`}>
                        <div className={`text-sm ${n.read_at ? 'font-normal' : 'font-semibold'}`}>{n.title}</div>
                        <div className="text-xs text-ink-muted">{n.message}</div>
                      </div>
                    ))
                  )}
                  <NavLink to="/notifications" onClick={() => setNotifOpen(false)}
                    className="block text-center text-xs mt-2 text-brand no-underline">
                    Ver todas
                  </NavLink>
                </div>
              )}
            </div>
            <NavLink to="/profile" className="text-sm text-ink-secondary no-underline hover:text-ink-primary">Perfil</NavLink>
            <button onClick={handleLogout} className="btn btn-sm btn-secondary">Sair</button>
          </div>
        </div>

        <div className="p-6 max-w-[1200px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
